import { usePageCrumbs } from '../lib/crumbs.ts';
import { Check, ChevronDown, ChevronRight, Search, SearchX } from '@orgus/ui/icons';
import { motion } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';
import { NavLink, Navigate, useNavigate, useParams } from 'react-router';
import { Button, Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, Input, Pressable, Sheet, SheetContent, SheetTitle, SheetTrigger, cn, motionPresets } from '@orgus/ui';
import { SETTINGS_ALIASES, SETTINGS_SECTIONS, filterSections, groupSections } from '../features/settings/registry.ts';
import type { SettingsSection } from '../features/settings/registry.ts';
import { WhenRuns } from '../features/server/ModuleAbsent.tsx';

/**
 * The sidebar: a search box, then the groups. Only the active section's group is open at rest
 * (ADR 0076: no long lists); a group opens on click, and a search opens every group it matches.
 * A group heading is a small dim caption led by its chevron; its sections sit indented beneath it.
 */
function SettingsNav({ active }: { active: SettingsSection }) {
  const [query, setQuery] = useState('');
  const groups = useMemo(() => groupSections(filterSections(SETTINGS_SECTIONS, query)), [query]);
  const activeGroup = active.group ?? 'workspace';
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set([activeGroup]));
  useEffect(() => setOpen((o) => (o.has(activeGroup) ? o : new Set([...o, activeGroup]))), [activeGroup]);
  const searching = query.trim() !== '';
  const toggle = (id: string) =>
    setOpen((o) => {
      const n = new Set(o);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  return (
    <nav aria-label="Settings sections" className="hidden w-56 shrink-0 flex-col gap-4 overflow-y-auto px-3 pb-8 pt-6 md:flex">
      <Input
        size="sm"
        aria-label="Find a setting"
        placeholder="approvals, tokens, theme"
        icon={<Search />}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setQuery('');
        }}
      />
      {groups.length === 0 ? (
        <div className="flex flex-col items-start gap-1 px-3 py-2">
          <p className="flex items-center gap-2 text-ui text-fg-subtle">
            <SearchX className="size-4 shrink-0" aria-hidden />
            No setting matches
          </p>
          <Button size="sm" variant="ghost" className="-ml-2" onClick={() => setQuery('')}>
            Clear search
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {groups.map((g) => {
            const shown = searching || open.has(g.id);
            return (
              <div key={g.id} className="flex flex-col">
                <h2>
                  <Pressable
                    aria-expanded={shown}
                    aria-controls={`settings-group-${g.id}`}
                    disabled={searching}
                    onClick={() => toggle(g.id)}
                    className="group flex h-control-sm w-full items-center gap-1 rounded-control px-2 text-caption font-medium text-fg-subtle hover:text-fg-muted disabled:opacity-100"
                  >
                    <ChevronRight
                      aria-hidden
                      className={cn(
                        'size-3.5 shrink-0 text-fg-subtle transition-transform duration-(--duration-fast) group-hover:text-fg-muted',
                        shown && 'rotate-90',
                        searching && 'opacity-0',
                      )}
                    />
                    <span className="min-w-0 flex-1 truncate text-left">{g.label}</span>
                    {!shown && <span className="text-fg-subtle num">{g.sections.length}</span>}
                  </Pressable>
                </h2>
                {shown && (
                  // The group's sections hang under its heading on a guide line, indented past the chevron.
                  <ul id={`settings-group-${g.id}`} className="ml-4 mt-1 flex flex-col border-l border-border-subtle pl-2">
                    {g.sections.map((s) => {
                      const on = s.id === active.id;
                      return (
                        <li key={s.id} className="relative">
                          {on && (
                            <motion.span layoutId="settings-nav" transition={motionPresets.move} className="selected-tint absolute inset-0 rounded-control" aria-hidden />
                          )}
                          <NavLink
                            to={`/settings/${s.id}`}
                            aria-current={on ? 'page' : undefined}
                            className={cn(
                              'relative flex h-control items-center rounded-control px-3 text-ui transition-colors duration-(--duration-fast)',
                              on ? 'font-medium text-fg' : 'text-fg-muted hover:bg-hover hover:text-fg',
                            )}
                          >
                            <span className="truncate">{s.title}</span>
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </nav>
  );
}

/**
 * Below 768 px the sidebar becomes one picker above the section. The closed field says
 * "Group: Section"; it opens a sheet with the settings search and the groups as headers,
 * so each section name appears once, bare.
 */
function SettingsPicker({ active }: { active: SettingsSection }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const groups = useMemo(() => groupSections(SETTINGS_SECTIONS), []);
  const group = groups.find((g) => g.sections.some((x) => x.id === active.id));
  return (
    <div className="px-4 pt-4 md:hidden">
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Pressable aria-label={`Settings section: ${active.title}. Change`} className="control-field flex h-control w-full items-center justify-between gap-2 px-3 text-left text-ui">
            <span className="min-w-0 truncate">
              {group && <span className="text-fg-subtle">{group.label}: </span>}
              <span className="text-fg">{active.title}</span>
            </span>
            <ChevronDown className="size-4 shrink-0 text-fg-subtle" aria-hidden />
          </Pressable>
        </SheetTrigger>
        <SheetContent side="bottom" showCloseButton={false} aria-describedby={undefined} className="pb-4">
          <SheetTitle className="sr-only">Settings sections</SheetTitle>
          <Command loop>
            <CommandInput placeholder="Find a setting" aria-label="Find a setting" autoFocus />
            <CommandList>
              <CommandEmpty>No setting matches</CommandEmpty>
              {groups.map((g) => (
                <CommandGroup key={g.id} heading={g.label}>
                  {g.sections.map((x) => (
                    <CommandItem
                      key={x.id}
                      value={`${g.label} ${x.title}`}
                      keywords={x.keywords}
                      onSelect={() => {
                        setOpen(false);
                        navigate(`/settings/${x.id}`);
                      }}
                      className="h-control-lg"
                    >
                      <span className="min-w-0 flex-1 truncate">{x.title}</span>
                      {x.id === active.id && <Check className="size-4 text-accent" aria-label="Current section" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </SheetContent>
      </Sheet>
    </div>
  );
}

/** Settings: grouped sections, one per feature, found in features/settings/sections/. */
export default function Settings() {
  const params = useParams();
  const id = (params['*'] ?? '').split('/')[0] ?? '';
  const first = SETTINGS_SECTIONS[0];
  const active = SETTINGS_SECTIONS.find((s) => s.id === id);
  usePageCrumbs(active ? [{ label: active.title }] : []);
  if (!active) {
    const alias = SETTINGS_ALIASES[id];
    if (alias) return <Navigate to={`/settings/${alias}`} replace />;
    return first ? <Navigate to={`/settings/${first.id}`} replace /> : null;
  }
  const Active = active.Component;
  return (
    <div className="flex h-full flex-col md:flex-row">
      <SettingsNav active={active} />
      <SettingsPicker active={active} />
      <div className="min-w-0 flex-1 md:overflow-y-auto">
        <section key={active.id} className="mx-auto flex w-full max-w-240 flex-col gap-8 px-4 pb-16 pt-4 md:px-8 md:pt-8" aria-labelledby="settings-title">
          <header className="motion-arrive flex flex-col gap-1">
            <h2 id="settings-title" className="text-title text-fg">
              {active.title}
            </h2>
            <p className="max-w-180 text-ui text-fg-muted">{active.description}</p>
          </header>
          <div className="motion-arrive flex min-w-0 flex-col">
            {active.needs ? (
              <WhenRuns {...active.needs}>
                <Active />
              </WhenRuns>
            ) : (
              <Active />
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
